import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useDeleteComunicado } from '@/hooks/use-comunicado-mutations';

type Props = { id: string };

const TRIGGER =
  'rounded-md p-1 text-text-tertiary transition-colors hover:bg-white/[0.04] hover:text-text-primary';
const MENU =
  'absolute right-0 top-7 z-10 flex min-w-[10rem] flex-col rounded-md border border-white/[0.08] bg-surface-elevated py-1 text-sm shadow-lg';
const ITEM =
  'flex items-center gap-sm px-md py-1.5 text-text-secondary transition-colors hover:bg-white/[0.04] hover:text-text-primary';
const DANGER_ITEM =
  'flex items-center gap-sm px-md py-1.5 text-error transition-colors hover:bg-error/10';

function useClickOutside(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, close]);
  return ref;
}

function useFeedDelete(id: string) {
  const del = useDeleteComunicado();
  return (): void => {
    if (!window.confirm('¿Eliminar este comunicado?')) return;
    del.mutate(id);
  };
}

function EditItem({ id, close }: { id: string; close: () => void }): ReactNode {
  const nav = useNavigate();
  return (
    <button
      type="button"
      className={ITEM}
      onClick={() => {
        close();
        nav(`/admin/communications/${id}/edit`);
      }}
    >
      <Pencil size={12} /> Editar
    </button>
  );
}

function DeleteItem({
  id,
  close,
}: {
  id: string;
  close: () => void;
}): ReactNode {
  const onDelete = useFeedDelete(id);
  return (
    <button
      type="button"
      className={DANGER_ITEM}
      onClick={() => {
        close();
        onDelete();
      }}
    >
      <Trash2 size={12} /> Eliminar
    </button>
  );
}

function MenuItems(p: { id: string; close: () => void }): ReactNode {
  return (
    <div className={MENU} role="menu">
      <EditItem id={p.id} close={p.close} />
      <DeleteItem id={p.id} close={p.close} />
    </div>
  );
}

export function FeedAdminActions({ id }: Props): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={TRIGGER}
        aria-label="Opciones"
      >
        <MoreVertical size={14} />
      </button>
      {open && <MenuItems id={id} close={() => setOpen(false)} />}
    </div>
  );
}
